import { createFileRoute } from "@tanstack/react-router";
import { PrinterIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  BowlingFlyerBack,
  BowlingFlyerFront,
} from "@/components/print/bowling-flyer";
import type { FlyerSideProps } from "@/components/print/flyer-sheet";
import {
  MinigolfFlyerBack,
  MinigolfFlyerFront,
} from "@/components/print/minigolf-flyer";
import {
  PapierfabrikFlyerInside,
  PapierfabrikFlyerOutside,
} from "@/components/print/papierfabrik-flyer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  A5_LANG,
  A5_LANG_ROLL_FOLD,
  formatSize,
  getSheetSize,
  type PrintFormat,
  ROLL_FOLD_PANELS,
  type Size,
} from "@/lib/print-formats";

/**
 * Internal tool for producing the printable flyer files. It is deliberately
 * kept out of the navigation, the sitemap and the search indexes – see the
 * `robots` meta below and the `pages` entry in `vite.config.ts`.
 */
export const Route = createFileRoute("/print/flyer")({
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: "Flyer Druckdaten – Alte Papierfabrik" },
      {
        name: "robots",
        content: "noindex, nofollow, noarchive, noimageindex, nosnippet",
      },
      { name: "googlebot", content: "noindex, nofollow" },
    ],
    // Montserrat is Aposto's corporate typeface and is only needed by their
    // panel, so it is loaded on this route rather than site-wide.
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap",
      },
    ],
  }),
});

type FlyerSide = {
  key: string;
  label: string;
  /** Panel breakdown shown under the preview, for folded sheets. */
  note?: string;
  Component: (props: FlyerSideProps) => React.JSX.Element;
};

type FlyerDefinition = {
  key: string;
  name: string;
  format: PrintFormat;
  /** Folded size, when it differs from the flat sheet. */
  closed?: Size;
  sides: FlyerSide[];
};

const FLYERS: FlyerDefinition[] = [
  {
    key: "papierfabrik",
    name: "Alte Papierfabrik – Wickelfalz",
    format: A5_LANG_ROLL_FOLD,
    closed: A5_LANG.trim,
    sides: [
      {
        key: "outside",
        label: "Außenseite",
        note: `v. l. n. r.: Einwickelseite ${ROLL_FOLD_PANELS.inner} mm · Rückseite ${ROLL_FOLD_PANELS.outer} mm · Titel ${ROLL_FOLD_PANELS.outer} mm`,
        Component: PapierfabrikFlyerOutside,
      },
      {
        key: "inside",
        label: "Innenseite",
        note: `v. l. n. r.: Aposto ${ROLL_FOLD_PANELS.outer} mm · Minigolf ${ROLL_FOLD_PANELS.outer} mm · Bowling ${ROLL_FOLD_PANELS.inner} mm`,
        Component: PapierfabrikFlyerInside,
      },
    ],
  },
  {
    key: "minigolf",
    name: "3D-Schwarzlicht Minigolf",
    format: A5_LANG,
    sides: [
      { key: "front", label: "Vorderseite", Component: MinigolfFlyerFront },
      { key: "back", label: "Rückseite", Component: MinigolfFlyerBack },
    ],
  },
  {
    key: "bowling",
    name: "Bowling",
    format: A5_LANG,
    sides: [
      { key: "front", label: "Vorderseite", Component: BowlingFlyerFront },
      { key: "back", label: "Rückseite", Component: BowlingFlyerBack },
    ],
  },
];

const getSheetId = (flyerKey: string, sideKey: string) =>
  `${flyerKey}-${sideKey}`;

const ALL_SHEET_IDS = FLYERS.flatMap((flyer) =>
  flyer.sides.map((side) => getSheetId(flyer.key, side.key))
);

const PRINT_FORMATS: PrintFormat[] = [A5_LANG, A5_LANG_ROLL_FOLD];

/** Largest the preview may get before it is scaled down further. */
const PREVIEW_MAX_SCALE = 0.52;
const PREVIEW_TARGET_WIDTH_PX = 900;
/** CSS reference pixels per millimetre, for sizing the previews. */
const CSS_PX_PER_INCH = 96;
const MM_PER_INCH = 25.4;
const PX_PER_MM = CSS_PX_PER_INCH / MM_PER_INCH;

function getPreviewScale(sheetWidthMm: number) {
  return Math.min(
    PREVIEW_MAX_SCALE,
    PREVIEW_TARGET_WIDTH_PX / (sheetWidthMm * PX_PER_MM)
  );
}

const getPageName = (formatId: string) => `fmt-${formatId}`;

/**
 * Print rules live in the document so `@page` can follow the bleed toggle.
 *
 * Each format gets its own named page, which lets one job hold sheets of
 * different sizes; the unnamed rule is set to the format of the current job so
 * a single-format job is unambiguous. Everything outside the job is removed
 * from the printed flow and the preview scaling is undone, so the sheets print
 * at their true physical size.
 */
function buildPrintCss(bleed: number, primary: PrintFormat) {
  const primarySize = getSheetSize(primary.trim, bleed);
  const namedPages = PRINT_FORMATS.map((format) => {
    const size = getSheetSize(format.trim, bleed);
    return `@page ${getPageName(format.id)} { size: ${size.width}mm ${size.height}mm; margin: 0; }`;
  }).join("\n");

  const pageAssignments = PRINT_FORMATS.map(
    (format) =>
      `  [data-print-format="${format.id}"] { page: ${getPageName(format.id)}; }`
  ).join("\n");

  return `
@page { size: ${primarySize.width}mm ${primarySize.height}mm; margin: 0; }
${namedPages}

@media print {
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }
  [data-print-hide] { display: none !important; }
  [data-print-flow] {
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
    gap: 0 !important;
    max-width: none !important;
    min-height: 0 !important;
    background: #ffffff !important;
  }
  [data-print-sheet] {
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  [data-print-sheet][data-print-inactive] { display: none !important; }
  [data-print-sheet][data-print-break] { break-before: page; }
  html { page: ${getPageName(primary.id)}; }
${pageAssignments}
  [data-print-frame] {
    width: auto !important;
    height: auto !important;
    overflow: visible !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  [data-print-scale] { transform: none !important; }
  [data-flyer-sheet], [data-flyer-sheet] * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;
}

function RouteComponent() {
  const [printJob, setPrintJob] = useState<{ sheetIds: string[] } | null>(null);
  const [withBleed, setWithBleed] = useState(true);
  const [showGuides, setShowGuides] = useState(false);

  const bleed = withBleed ? A5_LANG.bleed : 0;

  const activeSheetIds = useMemo(
    () => new Set(printJob?.sheetIds ?? ALL_SHEET_IDS),
    [printJob]
  );
  const firstActiveSheetId = ALL_SHEET_IDS.find((id) => activeSheetIds.has(id));

  // The unnamed `@page` follows the job when every sheet in it shares a
  // format, which is the case for every per-flyer and per-side button.
  const activeFormats = new Set(
    FLYERS.flatMap((flyer) =>
      flyer.sides
        .filter((side) => activeSheetIds.has(getSheetId(flyer.key, side.key)))
        .map(() => flyer.format)
    )
  );
  const primaryFormat =
    activeFormats.size === 1
      ? ([...activeFormats][0] ?? A5_LANG)
      : A5_LANG_ROLL_FOLD;

  const printCss = useMemo(
    () => buildPrintCss(bleed, primaryFormat),
    [bleed, primaryFormat]
  );

  useEffect(() => {
    if (!printJob) {
      return;
    }
    const finish = () => setPrintJob(null);
    window.addEventListener("afterprint", finish);
    // Wait for the hidden/unscaled layout to paint before opening the dialog.
    const frame = requestAnimationFrame(() => window.print());
    return () => {
      window.removeEventListener("afterprint", finish);
      cancelAnimationFrame(frame);
    };
  }, [printJob]);

  return (
    <div
      className="min-h-svh bg-background pb-24 text-foreground"
      data-print-flow
    >
      <style>{printCss}</style>

      <header
        className="sticky top-0 z-50 border-white/10 border-b bg-background/95 backdrop-blur"
        data-print-hide
      >
        <div className="container mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-4">
          <div>
            <h1 className="font-bold text-xl leading-tight">
              Flyer – Druckdaten
            </h1>
            <p className="text-sm opacity-70">
              {withBleed
                ? `Datenformate inkl. ${A5_LANG.bleed} mm Beschnitt`
                : "Endformate ohne Beschnittzugabe"}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={withBleed}
                id="with-bleed"
                onCheckedChange={setWithBleed}
              />
              <Label htmlFor="with-bleed">Beschnitt {A5_LANG.bleed} mm</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={showGuides}
                id="show-guides"
                onCheckedChange={setShowGuides}
              />
              <Label htmlFor="show-guides">Hilfslinien</Label>
            </div>
            <Button
              onClick={() => setPrintJob({ sheetIds: ALL_SHEET_IDS })}
              type="button"
            >
              <PrinterIcon />
              Alle drucken
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-7xl px-4 py-10" data-print-flow>
        <div
          className="mb-10 rounded-2xl border border-white/10 bg-white/5 p-5 text-sm leading-relaxed"
          data-print-hide
        >
          <p className="mb-2 font-bold">Hinweise für die Druckausgabe</p>
          <ul className="list-outside list-disc space-y-1 pl-4 opacity-80">
            <li>
              Im Druckdialog „Als PDF speichern“ wählen, Ränder auf „Keine“ und
              Skalierung auf 100 % stellen.
            </li>
            <li>
              „Hintergrundgrafiken“ aktivieren – sonst fehlen alle Farbflächen
              und Fotos.
            </li>
            <li>
              Die Flyer haben unterschiedliche Papierformate. Am sichersten ist
              es, jeden Flyer einzeln über seinen eigenen Druck-Button
              auszugeben.
            </li>
            <li>
              Hilfslinien (Beschnitt, Satzspiegel und Falzlinien) dienen nur der
              Kontrolle am Bildschirm und werden nie mitgedruckt.
            </li>
          </ul>
        </div>

        <div className="space-y-16" data-print-flow>
          {FLYERS.map((flyer) => {
            const sheetSize = getSheetSize(flyer.format.trim, bleed);
            const previewScale = getPreviewScale(sheetSize.width);

            return (
              <section data-print-flow key={flyer.key}>
                <div
                  className="mb-6 flex flex-wrap items-start justify-between gap-4"
                  data-print-hide
                >
                  <div>
                    <h2 className="font-bold text-2xl">{flyer.name}</h2>
                    <p className="mt-1 text-sm opacity-70">
                      {flyer.format.label} · Endformat
                      {flyer.closed ? " (geschlossen) " : " "}
                      {formatSize(flyer.closed ?? flyer.format.trim)}
                      {" · Datenformat "}
                      {formatSize(sheetSize)}
                    </p>
                  </div>
                  <Button
                    onClick={() =>
                      setPrintJob({
                        sheetIds: flyer.sides.map((side) =>
                          getSheetId(flyer.key, side.key)
                        ),
                      })
                    }
                    type="button"
                    variant="secondary"
                  >
                    <PrinterIcon />
                    Druckdatei drucken
                  </Button>
                </div>

                <div className="flex flex-wrap gap-8" data-print-flow>
                  {flyer.sides.map((side) => {
                    const sheetId = getSheetId(flyer.key, side.key);
                    const isActive = activeSheetIds.has(sheetId);

                    return (
                      <figure
                        className="flex flex-col gap-3"
                        data-print-break={
                          isActive && sheetId !== firstActiveSheetId
                            ? ""
                            : undefined
                        }
                        data-print-format={flyer.format.id}
                        data-print-inactive={isActive ? undefined : ""}
                        data-print-sheet
                        key={sheetId}
                      >
                        <figcaption
                          className="flex items-center justify-between gap-3"
                          data-print-hide
                        >
                          <span className="font-medium text-sm">
                            {side.label}
                          </span>
                          <Button
                            onClick={() => setPrintJob({ sheetIds: [sheetId] })}
                            size="sm"
                            type="button"
                            variant="ghost"
                          >
                            <PrinterIcon />
                            Seite drucken
                          </Button>
                        </figcaption>

                        <div
                          className="overflow-hidden bg-white shadow-2xl ring-1 ring-white/15"
                          data-print-frame
                          style={{
                            width: `calc(${sheetSize.width}mm * ${previewScale})`,
                            height: `calc(${sheetSize.height}mm * ${previewScale})`,
                          }}
                        >
                          <div
                            data-print-scale
                            style={{
                              transform: `scale(${previewScale})`,
                              transformOrigin: "top left",
                            }}
                          >
                            <side.Component bleed={bleed} guides={showGuides} />
                          </div>
                        </div>

                        {side.note ? (
                          <p
                            className="max-w-[36rem] text-xs opacity-60"
                            data-print-hide
                          >
                            {side.note}
                          </p>
                        ) : null}
                      </figure>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </main>
    </div>
  );
}
